<template>
  <div class="panel statement-panel">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon :icon="icon" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div>
              {{ $t(title) }}
              <font-awesome-icon icon="file"
                                 v-b-tooltip.hover
                                 :title="'${' + tag + '}'"/>
              <font-awesome-icon icon="exclamation-triangle"
                                 class="risk-6 ml-1"
                                 v-if="!project[field]"
                                 v-b-tooltip.hover
                                 :title="$t(missingLabel)"/>
            </div>
          </div>
        </div>
      </div>
      <div class="profile-compact__actions">
        <button type="button"
                class="btn btn-secondary btn-sm cursor-pointer mr-2"
                @click="chooseWordDocument"
                v-if="canEdit && !editing"
                :disabled="importing"
                :data-qid="qid + '-import-word'"
        >
          <font-awesome-icon :icon="importing ? 'spinner' : 'file-word'" :spin="importing" class="mr-1"/>
          {{ $t('projects.measures.import_word') }}
        </button>
        <span class="icon-btn"
              @click="edit"
              v-if="canEdit && !editing"
              :data-qid="qid + '-edit'"
        >
          <font-awesome-icon icon="pen"/>
        </span>
        <span class="icon-btn btn-delete"
              @click="cancel"
              v-if="editing"
              :data-qid="qid + '-cancel'"
        >
          <font-awesome-icon icon="rotate-left"/>
        </span>
        <span class="icon-btn btn-edit"
              @click="save"
              v-if="editing"
              :data-qid="qid + '-save'"
        >
          <font-awesome-icon icon="save"/>
        </span>
      </div>
      <input ref="wordInput"
             type="file"
             accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
             class="d-none"
             @change="importWordDocument"/>
    </header>

    <div class="panel__body">
      <!-- Sent as a task to documentation collection users (US 9394) -->
      <div v-if="showAssignment && (assignment.users.length || canEdit)" class="statement-panel__assignment" :data-qid="qid + '-assignment'">
        <span class="text-muted">{{ $t(assignment.users.length ? 'projects.measures.task_sent_to' : 'projects.measures.task_not_sent') }}</span>
        <span v-for="user in assignment.users" :key="'u-' + user.id" class="statement-panel__user">
          <Avatar :object="user" size="xs"/> {{ user.name }}
        </span>
        <span v-if="assignment.users.length && assignment.status"
              class="home-tag"
              :class="'is-' + statusColor"
              :data-qid="qid + '-task-status'"
        >{{ statusText }}</span>
        <span class="grow"></span>
        <button v-if="canEdit"
                type="button"
                class="btn btn-link btn-sm p-0"
                :data-qid="qid + '-assign'"
                @click="openAssign"
        >{{ $t('projects.measures.task_assign') }}</button>
        <button v-if="canEdit && assignment.status === 'delivered'"
                type="button"
                class="btn btn-link btn-sm p-0 ml-3"
                :data-qid="qid + '-send-back'"
                @click="openSendBack"
        >{{ $t('projects.measures.task_send_back') }}</button>
      </div>
      <div v-if="taskMessage" class="alert alert-warning mb-2" :data-qid="qid + '-task-message'">
        <b>{{ $t('projects.measures.task_sent_back_message') }}</b> {{ taskMessage }}
      </div>

      <div class="card">
        <div class="content-view-body">
          <RichTextEditor v-if="editing"
                          v-model="draft"
                          :rows="16"
                          :placeholder="$t(placeholder)"
                          :qid="qid + '-input'"
                          class="statement-panel__editor"/>
          <div v-else class="section-container statement-panel__text">
            <RichTextView v-if="project[field]" :value="project[field]"/>
            <p v-else class="text-muted mb-0">{{ $t('system.missing_input') }}</p>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-if="showAssignment"
             :ref="'assign-' + field"
             modal-class="v2-modal"
             :title="$t('projects.measures.task_assign_title', { text: $t(title) })"
             :ok-title="$t('system.save')"
             :cancel-title="$t('administration.users.create.cancel')"
             :ok-disabled="saving"
             @ok.prevent="saveAssign"
    >
      <p class="text-muted">{{ $t('projects.measures.task_assign_text') }}</p>
      <multiselect v-model="selectedUsers"
                   :options="userOptions"
                   :multiple="true"
                   :taggable="false"
                   track-by="id"
                   label="name"
                   :select-label="$t('system.dropdown_select_label')"
                   :selected-label="$t('system.dropdown_selected_label')"
                   :deselect-label="$t('system.dropdown_deselect_label')"
                   :placeholder="$t('projects.measures.task_assign_placeholder')"
                   :data-qid="qid + '-assign-users'"
      >
        <template slot="option" slot-scope="props">
          <Avatar :object="props.option" size="xs"/>
          <span class="ml-2">{{ props.option.name }}</span>
        </template>
      </multiselect>
    </b-modal>

    <b-modal v-if="showAssignment"
             :ref="'send-back-' + field"
             modal-class="v2-modal"
             :title="$t('projects.measures.task_send_back_title', { text: $t(title) })"
             :ok-title="$t('projects.measures.task_send_back')"
             :cancel-title="$t('administration.users.create.cancel')"
             :ok-disabled="saving || !sendBackMessage.trim()"
             @ok.prevent="saveSendBack"
    >
      <b-form-textarea v-model="sendBackMessage"
                       rows="4"
                       :placeholder="$t('projects.measures.task_send_back_placeholder')"
                       :data-qid="qid + '-send-back-message'"/>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import RichTextEditor from '@/components/richText/RichTextEditor.vue';
import RichTextView from '@/components/richText/RichTextView.vue';
import Avatar from '@/components/Avatar.vue';
import { richTextFromElement, rankHeadings } from '@/components/richText/richText';

// One half of the System description tab on control_assessment projects (US 9394):
// the management statement or the system description as rich text (see richText.js),
// which can also be imported from a Word document. With showAssignment, it can be sent as a task to
// documentation collection users; with statementTask, it is that task as its user sees it (from Home).
export default {
  name: 'StatementTextPanel',
  components: {
    RichTextEditor,
    RichTextView,
    Avatar
  },
  props: {
    project: {
      type: Object,
      required: true
    },
    // management_statement or system_description
    field: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    missingLabel: {
      type: String,
      required: true
    },
    // The tag that Word report templates use for this text
    tag: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: 'text'
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    qid: {
      type: String,
      required: true
    },
    // Shows who the text is sent to, and lets an auditor send it or send it back
    showAssignment: {
      type: Boolean,
      default: false
    },
    // The task (from statement-tasks/{id}) when its documentation collection user opens it from Home:
    // the text is saved through the task
    statementTask: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      editing: false,
      draft: '',
      assignment: { id: null, status: null, users: [] },
      userOptions: [],
      selectedUsers: [],
      sendBackMessage: '',
      saving: false,
      importing: false
    };
  },
  computed: {
    getClient() {
      return this.$store.state.client;
    },
    statusColor() {
      return { pending: 'grey', delivered: 'teal', returned: 'red' }[this.assignment.status] || 'grey';
    },
    statusText() {
      if (this.assignment.status === 'delivered') {
        return this.$t('projects.measures.task_status_delivered', {
          name: this.assignment.delivered_by ? this.assignment.delivered_by.name : '',
          date: this.$options.filters.formatDate(this.assignment.delivered_at)
        });
      }
      return this.$t('projects.measures.task_status_' + this.assignment.status);
    },
    // A text that was sent back shows its message to the user who writes it
    taskMessage() {
      return this.statementTask && this.statementTask.status === 'returned' ? this.statementTask.message : null;
    }
  },
  mounted() {
    if (this.showAssignment) {
      this.loadAssignment();
    }
  },
  methods: {
    loadAssignment() {
      piincHttp.get('projects/' + this.project.slug + '/statement-tasks').then((response) => {
        const task = (response.data || []).find((item) => item.field === this.field);
        if (task) {
          this.assignment = task;
        }
      }, () => {});
    },
    openAssign() {
      this.selectedUsers = this.assignment.users.slice();
      piincHttp.get('users', { params: { client: this.getClient.id, per_page: 100 } }).then((response) => {
        const users = response.data.items || [];
        // Keep users who have it already, also when they're not in the first page of users
        this.selectedUsers.forEach((user) => {
          if (!users.find((option) => option.id === user.id)) {
            users.push(user);
          }
        });
        this.userOptions = users;
      }, () => {});
      this.$refs['assign-' + this.field].show();
    },
    saveAssign() {
      this.saving = true;
      piincHttp.put('projects/' + this.project.slug + '/statement-tasks/' + this.field, {
        users: this.selectedUsers.map((user) => user.id)
      }).then((response) => {
        this.assignment = response.data;
        this.$refs['assign-' + this.field].hide();
        this.$toastr('success', this.$t('projects.measures.task_assign_success'));
      }, (error) => {
        this.$toastr('error', (error && error.data && error.data.message) || this.$t('projects.measures.save_failed'));
      }).finally(() => {
        this.saving = false;
      });
    },
    openSendBack() {
      this.sendBackMessage = '';
      this.$refs['send-back-' + this.field].show();
    },
    saveSendBack() {
      this.saving = true;
      piincHttp.post('statement-tasks/' + this.assignment.id + '/send-back', { message: this.sendBackMessage }).then((response) => {
        this.assignment = response.data;
        this.$refs['send-back-' + this.field].hide();
        this.$toastr('success', this.$t('projects.measures.task_send_back_success'));
      }, (error) => {
        this.$toastr('error', (error && error.data && error.data.message) || this.$t('projects.measures.save_failed'));
      }).finally(() => {
        this.saving = false;
      });
    },
    edit() {
      this.draft = this.project[this.field] || '';
      this.editing = true;
    },
    cancel() {
      this.editing = false;
      this.draft = '';
    },
    save() {
      const request = this.statementTask
        ? piincHttp.put('statement-tasks/' + this.statementTask.id, { text: this.draft })
          .then((response) => Object.assign({}, this.project, { [this.field]: response.data.text }))
        : piincHttp.put('projects/' + this.project.slug, { [this.field]: this.draft }).then((response) => response.data);
      request.then((project) => {
        this.editing = false;
        this.$emit('updated', project);
        this.$toastr('success', this.$t('projects.update.success'));
      }, (error) => {
        this.$toastr('error', (error && error.data && error.data.message) || this.$t('projects.measures.save_failed'));
      });
    },
    chooseWordDocument() {
      const current = this.project[this.field];
      if (!current) {
        this.$refs.wordInput.click();
        return;
      }
      // The import is only a draft until it's saved, but it would replace the text in the editor
      this.$bvModal.msgBoxConfirm(this.$t('projects.measures.import_word_replace_text'), {
        title: this.$t('projects.measures.import_word'),
        okTitle: this.$t('projects.measures.import_word_replace'),
        cancelTitle: this.$t('administration.users.create.cancel'),
        modalClass: 'v2-modal'
      }).then((confirmed) => {
        if (confirmed) {
          this.$refs.wordInput.click();
        }
      });
    },
    importWordDocument(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) {
        return;
      }
      if (!/\.docx$/i.test(file.name)) {
        this.$toastr('error', this.$t('projects.measures.import_word_failed'));
        return;
      }

      this.importing = true;
      file.arrayBuffer().then((arrayBuffer) => {
        return Promise.all([
          import('mammoth/mammoth.browser'),
          import('@/components/richText/wordListKinds')
        ]).then(([mammothModule, wordListKindsModule]) => {
          const mammoth = mammothModule.default || mammothModule;
          return Promise.all([
            mammoth.convertToHtml({ arrayBuffer }, {
              // Underline is left out by default. Title and subtitle are usually the document's name,
              // so they're normal text rather than headers.
              styleMap: [
                'u => u',
                "p[style-name='Title'] => p:fresh",
                "p[style-name='Subtitle'] => p:fresh"
              ],
              // Pictures are left out, so don't read them
              convertImage: mammoth.images.imgElement(() => Promise.resolve({ src: '' }))
            }),
            // mammoth makes every numbered list 1, 2, 3; this keeps lettered and roman lists
            wordListKindsModule.default(arrayBuffer).catch(() => [])
          ]);
        });
      }).then(([result, listKinds]) => {
        const body = new DOMParser().parseFromString(result.value, 'text/html').body;
        const items = body.querySelectorAll('li');
        if (items.length === listKinds.length) {
          items.forEach((item, index) => {
            const list = item.parentElement;
            if (list.nodeName === 'OL' && !list.hasAttribute('type') && listKinds[index] !== '1' && listKinds[index] !== 'bullet') {
              list.setAttribute('type', listKinds[index]);
            }
          });
        }

        // Header levels are ranked: the document's top level becomes Header 2, the rest Header 3
        const ranking = rankHeadings(body);
        // Only headers, lists, bold, italic, underline and line breaks are kept; everything else becomes plain text.
        // The editor redraws itself when its value is set from outside.
        this.draft = richTextFromElement(body, { headings: ranking.headings });
        this.editing = true;
        this.$toastr('success', this.$t('projects.measures.import_word_success'));
        if (ranking.merged.length) {
          this.$toastr('info', this.$t('projects.measures.import_word_headers_merged', { levels: ranking.merged.join(', ') }));
        }
      }).catch(() => {
        this.$toastr('error', this.$t('projects.measures.import_word_failed'));
      }).finally(() => {
        this.importing = false;
      });
    }
  }
};
</script>
